import { useTranslation } from 'react-i18next';
import {
  quotaPercent,
  quotaTextColorClass,
  quotaBarColorClass,
  formatQuotaLabel,
  formatQuotaResetDate,
  quotaExcessCount,
  type QuotaInfo
} from '../utils/quota-utils';

export interface UseExtractionQuotaResult {
  /** Usage percentage 0–100, or null for unlimited plans */
  pct: number | null;
  /** Whether the plan has no quota limit */
  isUnlimited: boolean;
  /** Tailwind text-color class (success/warn/error) */
  textColorClass: string;
  /** Tailwind background-color class for the progress bar fill */
  barColorClass: string;
  /** Formatted quota label e.g. "16 / 50" or "16 / ∞" */
  label: string;
  /** Localised reset date string e.g. "1 June 2026" */
  resetDate: string;
  /** Documents consumed beyond the monthly plan limit */
  excess: number;
}

/**
 * Derives all display values from a raw {@link QuotaInfo} object.
 *
 * Encapsulates colour logic, label formatting, and date formatting so that
 * {@link ExtractionQuotaCard} stays purely presentational.
 */
export function useExtractionQuota(quota: QuotaInfo): UseExtractionQuotaResult {
  const { i18n } = useTranslation();

  const pct = quotaPercent(quota);

  return {
    pct,
    isUnlimited: pct === null,
    textColorClass: quotaTextColorClass(pct),
    barColorClass: quotaBarColorClass(pct),
    label: formatQuotaLabel(quota),
    resetDate: formatQuotaResetDate(quota, i18n.language),
    excess: quotaExcessCount(quota)
  };
}
